import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Settings, Trash2, AlertCircle } from "lucide-react";
import { useVariableMappings } from "@/hooks/useVariableMappings";
import { useCalculations } from "@/hooks/useCalculations";
import { ExtractedVariable, CLIENT_FIELD_OPTIONS } from "@/types/variable-mappings";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { VariableMappingForm } from "./VariableMappingForm";

interface VariableMappingManagerProps {
  templateId: number;
  extractedVariables: ExtractedVariable[];
  jotformFormId?: string | null;
  jotformFormIdReferral?: string | null;
}

export function VariableMappingManager({
  templateId,
  extractedVariables,
  jotformFormId,
  jotformFormIdReferral,
}: VariableMappingManagerProps) {
  const {
    mappings,
    isLoading,
    deleteMapping,
    isDeleting,
    getMappingForVariable,
  } = useVariableMappings(templateId);

  const { calculations } = useCalculations(templateId);

  const [selectedVariable, setSelectedVariable] = useState<ExtractedVariable | null>(null);
  const [showMappingDialog, setShowMappingDialog] = useState(false);

  const handleAddMapping = (variable: ExtractedVariable) => {
    setSelectedVariable(variable);
    setShowMappingDialog(true);
  };

  const handleEditMapping = (variable: ExtractedVariable) => {
    setSelectedVariable(variable);
    setShowMappingDialog(true);
  };

  const handleDeleteMapping = async (variableName: string) => {
    const mapping = getMappingForVariable(variableName);
    if (mapping) {
      await deleteMapping(mapping.id);
    }
  };

  const handleCloseDialog = () => {
    setShowMappingDialog(false);
    setSelectedVariable(null);
  };

  // Transform variables to ensure they have the correct structure
  const normalizedVariables = extractedVariables.map((v: any) => {
    const name = v.name || v.variable_name;
    return {
      name,
      placeholder: v.placeholder || `{${name}}`,
      occurrences: v.occurrences || 1,
      slides: v.slides || [],
      mapped: !!getMappingForVariable(name),
    } as ExtractedVariable;
  });

  // Stats
  const totalVariables = normalizedVariables.length;
  const mappedCount = normalizedVariables.filter(v => v.mapped).length;
  const unmappedCount = totalVariables - mappedCount;

  const unmappedVariables = normalizedVariables.filter((v) => !v.mapped);
  const mappedVariables = normalizedVariables.filter((v) => v.mapped);

  // Detect orphaned mappings (mappings that reference variables not in PPT)
  const extractedVariableNames = normalizedVariables.map(v => v.name);
  const orphanedMappings = mappings?.filter(
    m => !extractedVariableNames.includes(m.variable_name)
  ) || [];

  // Detect orphaned calculation mappings (mappings that reference deleted calculations)
  const orphanedCalculationMappings = mappings?.filter(m => {
    if (m.source_type !== 'calculation') return false;

    // Extract calculation_id from calculation_config
    const calcId = m.calculation_config?.calculation_id;
    if (!calcId) return false;

    // Check if calculation still exists
    return !calculations?.find(c => c.id === calcId);
  }) || [];

  console.log('VariableMappingManager Debug:', {
    extractedVariables,
    normalizedVariables,
    mappings,
    unmappedVariables,
    mappedVariables,
    orphanedCalculationMappings
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Cargando mapeos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Warning when no JotForm is selected */}
      {!jotformFormId && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Esta plantilla no tiene un formulario de JotForm asociado. Por favor, edita la plantilla 
            y selecciona un formulario para poder mapear las variables correctamente.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Variables</CardDescription>
            <CardTitle className="text-3xl">{totalVariables}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Mapeadas</CardDescription>
            <CardTitle className="text-3xl text-green-600">{mappedCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Sin Mapear</CardDescription>
            <CardTitle className="text-3xl text-orange-600">{unmappedCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Unmapped Variables Alert */}
      {unmappedCount > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Tienes {unmappedCount} variable{unmappedCount > 1 ? 's' : ''} sin mapear.
            Asigna una pregunta o cálculo a cada variable para poder generar reportes dinámicos.
          </AlertDescription>
        </Alert>
      )}

      {/* Orphaned Mappings Alert */}
      {orphanedMappings.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Tienes {orphanedMappings.length} mapeo{orphanedMappings.length > 1 ? 's' : ''} huérfano{orphanedMappings.length > 1 ? 's' : ''}.
            Estas variables ya no existen en el archivo PPT pero aún tienen configuración en la base de datos.
          </AlertDescription>
        </Alert>
      )}

      {/* Orphaned Calculation Mappings Alert */}
      {orphanedCalculationMappings.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Tienes {orphanedCalculationMappings.length} mapeo{orphanedCalculationMappings.length > 1 ? 's' : ''} que referencia{orphanedCalculationMappings.length > 1 ? 'n' : ''} cálculos eliminados.
            {orphanedCalculationMappings.length > 1 ? ' Estas variables' : ' Esta variable'} usa{orphanedCalculationMappings.length > 1 ? 'n' : ''} cálculos que ya no existen en la pestaña de Cálculos.
            Actualiza {orphanedCalculationMappings.length > 1 ? 'estos mapeos' : 'este mapeo'} para seleccionar un cálculo válido o elimínalos.
          </AlertDescription>
        </Alert>
      )}

      {/* Unmapped Variables */}
      {unmappedVariables.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Variables Sin Mapear</CardTitle>
            <CardDescription>
              Estas variables aún no tienen una fuente de datos asignada
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Variable</TableHead>
                  <TableHead>Placeholder</TableHead>
                  <TableHead>Apariciones</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmappedVariables.map((variable) => (
                  <TableRow key={variable.name}>
                    <TableCell className="font-medium">{variable.name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {variable.placeholder}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{variable.occurrences}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleAddMapping(variable)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Mapear
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Orphaned Mappings */}
      {orphanedMappings.length > 0 && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Mapeos Huérfanos</CardTitle>
            <CardDescription>
              Estas configuraciones referencian variables que ya no existen en el archivo PPT
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Variable</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Fuente</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphanedMappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      <div className="font-medium text-destructive">{mapping.variable_name}</div>
                      <code className="text-xs text-muted-foreground">
                        {`{${mapping.variable_name}}`}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {mapping.source_type === 'question' ? 'Pregunta' :
                         mapping.source_type === 'client' ? 'Cliente' :
                         'Cálculo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {mapping.source_type === 'question' ? mapping.question?.name :
                       mapping.source_type === 'client' ? 'Campo del cliente' :
                       mapping.calculation_type}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteMapping(mapping.id)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Mapped Variables */}
      {mappedVariables.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Variables Mapeadas</CardTitle>
            <CardDescription>
              Variables con fuente de datos asignada
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Variable</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Fuente</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappedVariables.map((variable) => {
                  const mapping = getMappingForVariable(variable.name);
                  if (!mapping) return <TableRow key={variable.name} style={{ display: 'none' }} />;

                  return (
                    <TableRow key={variable.name}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{variable.name}</div>
                          <code className="text-xs text-muted-foreground">
                            {variable.placeholder}
                          </code>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          mapping.source_type === 'question' ? 'default' :
                          mapping.source_type === 'client' ? 'outline' :
                          'secondary'
                        }>
                          {mapping.source_type === 'question' ? 'Pregunta' :
                           mapping.source_type === 'client' ? 'Cliente' :
                           'Cálculo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {mapping.source_type === 'question' ? (
                          <div className="text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{mapping.question?.name}</span>
                              {jotformFormIdReferral && mapping.form_source && (
                                <Badge variant={mapping.form_source === 'main' ? 'default' : 'secondary'} className="text-xs">
                                  {mapping.form_source === 'main' ? 'Principal' : 'Referido'}
                                </Badge>
                              )}
                            </div>
                            <div className="text-muted-foreground">
                              Grupo: {mapping.question_group}
                            </div>
                          </div>
                        ) : mapping.source_type === 'client' ? (
                          <div className="text-sm">
                            <div className="font-medium">
                              {(() => {
                                const clientConfig = mapping.calculation_config as any;
                                const clientField = clientConfig?.client_field;
                                return CLIENT_FIELD_OPTIONS.find(opt => opt.value === clientField)?.label || 'Campo del Cliente';
                              })()}
                            </div>
                            <div className="text-muted-foreground text-xs space-y-1">
                              <div>
                                {(() => {
                                  const clientConfig = mapping.calculation_config as any;
                                  const clientField = clientConfig?.client_field;
                                  return CLIENT_FIELD_OPTIONS.find(opt => opt.value === clientField)?.description;
                                })()}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                Resolución automática: submission → lanzamiento → cliente
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm">
                            <div className="font-medium capitalize">
                              {mapping.calculation_type?.replace('-', ' ')}
                            </div>
                            <div className="text-muted-foreground">
                              {mapping.calculation_config && 'group' in mapping.calculation_config && (
                                <>Grupo: {mapping.calculation_config.group}</>
                              )}
                              {mapping.calculation_config && 'individualGroup' in mapping.calculation_config && (
                                <>Individual: {mapping.calculation_config.individualGroup}, Referencia: {mapping.calculation_config.referenceGroup}</>
                              )}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditMapping(variable)}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMapping(variable.name)}
                            disabled={isDeleting}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedVariable && getMappingForVariable(selectedVariable.name)
                ? 'Editar Mapeo'
                : 'Nuevo Mapeo'}
            </DialogTitle>
            <DialogDescription>
              Configura la fuente de datos para la variable{' '}
              <code className="text-xs bg-muted px-2 py-1 rounded">
                {selectedVariable?.placeholder}
              </code>
            </DialogDescription>
          </DialogHeader>

          {selectedVariable && (
            <VariableMappingForm
              templateId={templateId}
              variable={selectedVariable}
              existingMapping={getMappingForVariable(selectedVariable.name)}
              onSuccess={handleCloseDialog}
              onCancel={handleCloseDialog}
              jotformFormId={jotformFormId}
              jotformFormIdReferral={jotformFormIdReferral}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
